import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import {
  Users,
  MapPin,
  Calendar,
  ShieldCheck,
  Sparkles,
  HardHat,
  Camera,
  GraduationCap,
  Palette,
  Music,
  Dumbbell,
  BadgeCheck,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CrewActivityPulse } from '@/components/CrewBadge';
import { CrewNip05Badge } from '@/components/CrewNip05Badge';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { crewAccent, crewBadge, type CrewActivity } from '@/lib/crewHelpers';
import { CREW_FOCUS_LABELS, CREW_VIBE_LABELS, type CrewFocus, type SkateCrew } from '@/lib/types';
import { cn } from '@/lib/utils';

const FOCUS_ICON: Record<CrewFocus, typeof Users> = {
  mixed: Sparkles,
  builder: HardHat,
  photo: Camera,
  coaching: GraduationCap,
  art: Palette,
  music: Music,
  wellness: Dumbbell,
};

const REP_STYLES: Record<string, { chip: string; glow: string }> = {
  og: { chip: 'bg-amber-500/20 text-amber-200 border-amber-400/40', glow: 'shadow-amber-500/30' },
  verified: { chip: 'bg-teal-500/20 text-teal-200 border-teal-400/40', glow: 'shadow-teal-500/30' },
  established: { chip: 'bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-400/40', glow: 'shadow-fuchsia-500/30' },
  crew: { chip: 'bg-white/10 text-gray-200 border-white/20', glow: 'shadow-white/10' },
};

/**
 * Immersive, team-page-style hero for a crew.
 *
 * - Full-bleed banner from `crew.image` (or a generated accent gradient when
 *   none is set), overlaid with the crew's deterministic accent colour so no two
 *   crews look alike.
 * - Big team name, reputation crown, live activity pulse, focus + verification.
 * - The crew's logo sits over the banner like a real club crest.
 *
 * Actions (invite/edit/join) are rendered by the parent via `actions` so the
 * hero stays purely presentational.
 */
export function CrewHero({
  crew,
  activity,
  confirmedCount,
  actions,
}: {
  crew: SkateCrew;
  activity?: CrewActivity;
  confirmedCount?: number;
  actions?: React.ReactNode;
}) {
  const accent = crewAccent(crew);
  const founder = useAuthor(crew.pubkey);
  const founderName = founder.data?.metadata?.name ?? genUserName(crew.pubkey);
  const founderPic = founder.data?.metadata?.picture;
  const npub = nip19.npubEncode(crew.pubkey);

  const rep = crewBadge(crew, confirmedCount);
  const repStyle = REP_STYLES[rep.key] ?? REP_STYLES.crew;
  const FocusIcon = FOCUS_ICON[crew.focus];

  return (
    <section className="relative overflow-hidden rounded-3xl border border-white/10 bg-black">
      {/* ── Banner ── */}
      <div className="relative h-40 w-full sm:h-56">
        {crew.image ? (
          <img
            src={crew.image}
            alt={crew.name}
            className="h-full w-full object-cover"
            style={{ filter: 'saturate(1.05)' }}
          />
        ) : (
          <div
            className="h-full w-full"
            style={{
              background: `radial-gradient(120% 140% at 20% 0%, ${accent.hex}55 0%, ${accent.hex}18 40%, #000 100%)`,
            }}
          />
        )}

        {/* Accent wash + bottom fade so text is always legible */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to top, #000 6%, ${accent.hex}14 55%, transparent 100%)`,
          }}
        />
        <div
          aria-hidden
          className="absolute -left-16 -top-16 h-56 w-56 rounded-full blur-3xl opacity-40"
          style={{ background: accent.hex }}
        />

        {/* Top-right floating rep + activity */}
        <div className="absolute right-3 top-3 flex flex-wrap items-center justify-end gap-1.5">
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-black uppercase tracking-wide backdrop-blur-md shadow-lg',
              repStyle.chip,
              repStyle.glow,
            )}
            title={rep.label}
          >
            <span className="leading-none">{rep.emoji}</span> {rep.label}
          </span>
          {activity && activity.level !== 'dormant' && (
            <span className="inline-flex items-center rounded-full border border-white/10 bg-black/50 px-2.5 py-1 backdrop-blur-md">
              <CrewActivityPulse activity={activity} showDormant />
            </span>
          )}
        </div>
      </div>

      {/* ── Identity row (logo overlaps the banner) ── */}
      <div className="relative px-4 pb-5 sm:px-6">
        <div className="-mt-12 flex items-end gap-4 sm:-mt-14">
          <div
            className="relative shrink-0 rounded-3xl p-[3px] shadow-2xl"
            style={{ background: `linear-gradient(135deg, ${accent.hex}, ${accent.hex}44)` }}
          >
            <Avatar className="h-24 w-24 rounded-[calc(1.5rem-3px)] border-4 border-black sm:h-28 sm:w-28">
              <AvatarImage src={crew.image} alt={crew.name} className="object-cover" />
              <AvatarFallback
                className="rounded-[calc(1.5rem-3px)] text-3xl font-black"
                style={{ color: accent.hex, background: `${accent.hex}1f` }}
              >
                {crew.name.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            {/* member count crest */}
            <span
              className="absolute -bottom-2 -right-2 inline-flex items-center gap-1 rounded-full border-2 border-black px-2 py-0.5 text-xs font-black text-black shadow-lg"
              style={{ background: accent.hex }}
            >
              <Users className="h-3 w-3" />
              {crew.members.length}
            </span>
          </div>

          {/* Focus tag floats to the right on desktop */}
          <div className="hidden flex-1 items-center justify-end pb-2 sm:flex">
            <span
              className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-black uppercase tracking-wide"
              style={{ color: accent.hex, borderColor: `${accent.hex}55`, background: `${accent.hex}14` }}
            >
              <FocusIcon className="h-3.5 w-3.5" />
              {CREW_FOCUS_LABELS[crew.focus]}
            </span>
          </div>
        </div>

        {/* Name + meta */}
        <div className="mt-4">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1
              className="text-3xl font-black uppercase leading-none text-white sm:text-4xl"
              style={{
                fontFamily: 'Impact, "Arial Black", sans-serif',
                textShadow: `0 0 24px ${accent.hex}55`,
              }}
            >
              {crew.name}
            </h1>
            <CrewNip05Badge nip05={crew.nip05} founderPubkey={crew.pubkey} />
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-gray-400">
            {crew.location && (
              <span className="flex min-w-0 items-center gap-1.5">
                <MapPin className="h-4 w-4 shrink-0" style={{ color: accent.hex }} />
                <span className="truncate">{crew.location}</span>
              </span>
            )}
            {crew.founded && (
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 shrink-0" /> Est. {crew.founded}
              </span>
            )}
            {/* Team leader inline */}
            <Link
              to={`/${npub}`}
              className="group flex items-center gap-1.5 transition-colors hover:text-white"
              title={`Team leader: ${founderName}`}
            >
              <Avatar className="h-5 w-5 border border-white/20">
                <AvatarImage src={founderPic} alt={founderName} />
                <AvatarFallback className="text-[8px]">{founderName.slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <span className="inline-flex items-center gap-1">
                <ShieldCheck className="h-3.5 w-3.5" style={{ color: accent.hex }} />
                Led by <span className="font-bold text-gray-200 group-hover:text-white">{founderName}</span>
              </span>
            </Link>
          </div>

          {/* Mobile focus tag */}
          <div className="mt-3 flex flex-wrap items-center gap-1.5 sm:hidden">
            <span
              className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black uppercase tracking-wide"
              style={{ color: accent.hex, borderColor: `${accent.hex}55`, background: `${accent.hex}14` }}
            >
              <FocusIcon className="h-3 w-3" />
              {CREW_FOCUS_LABELS[crew.focus]}
            </span>
          </div>

          {/* Vibes */}
          {crew.vibes.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {crew.vibes.slice(0, 6).map((v) => (
                <span
                  key={v}
                  className="rounded-full border border-white/10 bg-white/[0.06] px-2.5 py-0.5 text-xs font-bold text-gray-300"
                >
                  {CREW_VIBE_LABELS[v] || v}
                </span>
              ))}
            </div>
          )}

          {/* Bio */}
          {crew.bio && (
            <p className="mt-4 max-w-2xl whitespace-pre-wrap break-words text-[15px] leading-relaxed text-gray-300">
              {crew.bio}
            </p>
          )}

          {/* Actions */}
          {actions && <div className="mt-5 flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      </div>
    </section>
  );
}

/** Tiny verified tick used elsewhere; re-exported so callers don't re-import lucide. */
export { BadgeCheck as CrewVerifiedTick };
